import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { Input, Button, Toggle, CurrencyPicker } from '../design-system';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { businessCurrency } from '../../utils/businessDefaults';

export interface CatalogueFormData {
  name: string;
  description?: string;
  currency: string;
  locale: string;
  isActive: boolean;
}

export interface CatalogueFormProps {
  initialData?: Partial<CatalogueFormData>;
  onSubmit: (data: CatalogueFormData) => void;
  onCancel: () => void;
  submitLabel?: string;
  loading?: boolean;
}

export const CatalogueForm: React.FC<CatalogueFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save Catalogue',
  loading = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const [formData, setFormData] = useState<CatalogueFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    currency: initialData?.currency || businessCurrency(),
    locale: initialData?.locale || 'en_US',
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        currency: initialData.currency || businessCurrency(),
        locale: initialData.locale || 'en_US',
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
      });
    }
  }, [initialData]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Catalogue name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Catalogue name must be at least 2 characters';
    }

if (formData.locale && !formData.locale.includes('_')) {
      newErrors.locale = 'Locale must be in format like en_US or ar_EG';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  const updateField = (field: keyof CatalogueFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.form}>
        {/* Name */}
        <View style={styles.field}>
          <Input
            label="Catalogue Name *"
            value={formData.name}
            onChangeText={(text) => updateField('name', text)}
            placeholder="e.g., Main Product Catalogue"
            error={errors.name}
            autoCapitalize="words"
            maxLength={100}
          />
        </View>

        {/* Description */}
        <View style={styles.field}>
          <Input
            label="Description"
            value={formData.description}
            onChangeText={(text) => updateField('description', text)}
            placeholder="Brief description of this catalogue"
            multiline
            numberOfLines={3}
            maxLength={500}
          />
        </View>

        {/* Currency */}
        <View style={styles.field}>
          <CurrencyPicker
            label="Currency *"
            value={formData.currency}
            onChange={(code) => updateField('currency', code)}
            error={errors.currency}
          />
        </View>

        {/* Locale */}
        <View style={styles.field}>
          <Input
            label="Locale *"
            value={formData.locale}
            onChangeText={(text) => updateField('locale', text)}
            placeholder="en_US"
            error={errors.locale}
            helperText="Locale code (e.g., en_US, ar_EG, fr_FR)"
          />
        </View>

        {/* Active Status */}
        <View style={styles.field}>
          <View style={styles.switchContainer}>
            <Text style={styles.switchLabel}>Active</Text>
            <Text style={styles.switchHelper}>
              {formData.isActive
                ? 'This catalogue is active and can be used'
                : 'This catalogue is inactive and hidden'}
            </Text>
          </View>
          <View style={styles.toggleContainer}>
            <Toggle
              value={formData.isActive}
              onValueChange={(value) => updateField('isActive', value)}
            />
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          <Button
            title="Cancel"
            onPress={onCancel}
            variant="outline"
            style={styles.cancelButton}
            disabled={loading}
          />
          <Button
            title={submitLabel}
            onPress={handleSubmit}
            variant="primary"
            loading={loading}
            disabled={loading}
            style={styles.submitButton}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
  },
  form: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    ...designTokens.shadows.sm,
  },
  field: {
    marginBottom: spacing.md,
  },
  switchContainer: {
    marginBottom: spacing.xs,
  },
  switchLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  switchHelper: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  toggleContainer: {
    marginTop: spacing.sm,
    alignItems: 'flex-start',
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
});

